/**
 * Refresh Store — Refetch Registry + Zustand Timestamp Store
 *
 * Two-part model for live update badges:
 * 1. Refetch Registry (plain Map): data hooks register their refetch()
 *    on mount and unregister on unmount. Not reactive.
 * 2. Zustand Store: data hooks write lastFetched timestamps after each
 *    successful fetch. Reactive — drives timestamp display in badges.
 */

import { create } from "zustand";
import { subscribeWithSelector } from "zustand/middleware";

// ─── Refetch Registry (non-reactive, imperative) ────────────────────────────

const refetchRegistry = new Map<string, () => Promise<void>>();

export function registerRefetch(
  componentId: string,
  fn: () => Promise<void>,
): void {
  refetchRegistry.set(componentId, fn);
}

export function unregisterRefetch(componentId: string): void {
  refetchRegistry.delete(componentId);
}

export function getRefetch(
  componentId: string,
): (() => Promise<void>) | undefined {
  return refetchRegistry.get(componentId);
}

// ─── Zustand Store (reactive, for timestamp display) ────────────────────────

interface RefreshStoreState {
  /** componentId -> epoch ms of last successful fetch */
  componentTimestamps: Record<string, number>;
  /** scopeContainerId -> whether a scoped refresh is in progress */
  scopeRefreshing: Record<string, boolean>;

  registerTimestamp(componentId: string, timestamp: Date): void;
  unregisterTimestamp(componentId: string): void;
  setScopeRefreshing(scopeId: string, refreshing: boolean): void;
}

export const useRefreshStore = create<RefreshStoreState>()(
  subscribeWithSelector((set) => ({
    componentTimestamps: {},
    scopeRefreshing: {},

    registerTimestamp: (componentId, timestamp) =>
      set((state) => ({
        componentTimestamps: {
          ...state.componentTimestamps,
          [componentId]: timestamp.getTime(),
        },
      })),

    unregisterTimestamp: (componentId) =>
      set((state) => {
        const { [componentId]: _, ...rest } = state.componentTimestamps;
        return { componentTimestamps: rest };
      }),

    setScopeRefreshing: (scopeId, refreshing) =>
      set((state) => ({
        scopeRefreshing: {
          ...state.scopeRefreshing,
          [scopeId]: refreshing,
        },
      })),
  })),
);

// ─── Selectors ──────────────────────────────────────────────────────────────

export const useComponentTimestamp = (
  id: string | undefined,
): number | undefined =>
  useRefreshStore(
    (s) => (id ? s.componentTimestamps[id] : undefined),
  );

export const useScopeRefreshing = (
  scopeId: string | undefined,
): boolean =>
  useRefreshStore(
    (s) => (scopeId ? s.scopeRefreshing[scopeId] ?? false : false),
  );

// ─── Automation Rules: WebSocket → Refetch Bridge ────────────────────────────

let automationRefreshListenerInitialized = false;

/**
 * Listen for server-side automation rule refresh events via WebSocket.
 * Bridges the 'component-data-refresh' event to the refetch registry.
 * Call once at app startup.
 */
export function initAutomationRefreshListener(): void {
  if (automationRefreshListenerInitialized) return;
  automationRefreshListenerInitialized = true;

  // Lazy import to avoid circular dependencies
  import('@/services/websocket/socketClient').then(({ socketClient }) => {
    socketClient.on('component-data-refresh', (data: { componentId: string }) => {
      const refetch = getRefetch(data.componentId);
      if (refetch) {
        refetch().catch(console.error);
      }
    });
  }).catch(() => {
    // Socket client not available — ignore
  });
}
